import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import {
  CalendarBlank, Clock, MapPin, Check, X, Heart,
  PaperPlaneTilt, Confetti, Star, Warning, Sparkle, DownloadSimple, PencilSimple
} from '@phosphor-icons/react';
import { PartyCountdown } from '@/components/PartyCountdown';
import { apiOrigin } from '@/utils/apiBase';

const API = apiOrigin();

// ---- Conditional rule evaluator (matches backend ShowIfRule schema) ----
function getSourceAnswer(fieldId, form) {
  if (fieldId === 'can_attend') return form.can_attend || '';
  if (fieldId === 'accompanying') return form.accompanying || [];
  if (fieldId === 'dietary') return form.dietary || '';
  if (fieldId === 'parent_name') return form.parent_name || '';
  if (fieldId === 'parent_phone') return form.parent_phone || '';
  if (fieldId === 'child_name') return form.child_name || '';
  return form.custom_responses?.[fieldId];
}

function evalRule(rule, form) {
  const ans = getSourceAnswer(rule.field, form);
  const ansStr = Array.isArray(ans) ? ans.join(',') : (ans == null ? '' : String(ans));
  const ansArr = Array.isArray(ans) ? ans : (ansStr ? [ansStr] : []);
  const target = rule.value || '';
  switch (rule.op) {
    case 'any':
      return ansArr.length > 0 && ansStr.trim() !== '';
    case 'equals':
      if (Array.isArray(ans)) return ans.includes(target);
      return ansStr === target;
    case 'not_equals':
      if (Array.isArray(ans)) return !ans.includes(target);
      return ansStr !== target;
    case 'contains':
      return ansStr.toLowerCase().includes(target.toLowerCase());
    case 'not_contains':
      return !ansStr.toLowerCase().includes(target.toLowerCase());
    case 'gt': return Number(ansStr) > Number(target);
    case 'lt': return Number(ansStr) < Number(target);
    case 'gte': return Number(ansStr) >= Number(target);
    case 'lte': return Number(ansStr) <= Number(target);
    case 'between':
      return Number(ansStr) >= Number(target) && Number(ansStr) <= Number(rule.value2 || 0);
    default: return true;
  }
}

function shouldShowField(field, form) {
  // New rules-based engine
  const rules = field.show_if_rules || [];
  if (rules.length > 0) {
    const logic = field.show_if_logic === 'or' ? 'or' : 'and';
    const results = rules.map(r => evalRule(r, form));
    return logic === 'or' ? results.some(Boolean) : results.every(Boolean);
  }
  // Legacy fallback (single show_if_field/value pair)
  const legacySrc = field.show_if_field;
  if (legacySrc) {
    const ans = getSourceAnswer(legacySrc, form);
    const target = field.show_if_value;
    if (target) {
      if (Array.isArray(ans)) return ans.includes(target);
      return String(ans || '') === target;
    }
    if (Array.isArray(ans)) return ans.length > 0;
    return Boolean(ans);
  }
  return true;
}

export default function PartyRSVPPage() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [thankYou, setThankYou] = useState('');
  const [rsvpResult, setRsvpResult] = useState(null); // {rsvp_id, personal_token, gallery_enabled, gallery_face_match_enabled}
  const [confettiKey, setConfettiKey] = useState(0); // Increments to trigger burst
  const [cakeKey, setCakeKey] = useState(0);
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const pageRef = useRef(null);

  // Form state
  const [form, setForm] = useState({
    child_name: '',
    parent_name: '',
    parent_phone: '',
    can_attend: '',
    accompanying: [],
    accompanying_other: '',
    dietary: '',
    dietary_other: '',
    custom_responses: {},
  });

  // Edit mode (via ?edit=token URL param) and "already RSVP'd" detection state
  const [editToken, setEditToken] = useState('');
  const [duplicateInfo, setDuplicateInfo] = useState(null); // {personal_token, child_name, can_attend}

  // ---- Draft auto-save ----
  // A stable per-(slug, device) token so the host can see incomplete forms
  // and the guest can resume on the same device.
  const draftTokenRef = useRef('');
  const [draftStatus, setDraftStatus] = useState('idle'); // idle | saving | saved | error
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const draftTimerRef = useRef(null);
  const draftStartedRef = useRef(false); // becomes true once the user touches anything

  if (!draftTokenRef.current && typeof window !== 'undefined') {
    try {
      const key = `rsvp_draft_token_${slug}`;
      let tk = localStorage.getItem(key);
      if (!tk) {
        tk = (crypto?.randomUUID?.() || `dt_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`);
        localStorage.setItem(key, tk);
      }
      draftTokenRef.current = tk;
    } catch { /* noop */ }
  }

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const res = await axios.get(`${API}/api/events/public/${slug}`);
        setEvent(res.data);
        // Check for ?edit= URL param
        const params = new URLSearchParams(window.location.search);
        const tk = params.get('edit');
        if (tk) {
          try {
            const r2 = await axios.get(`${API}/api/events/public/${slug}/rsvp/edit/${tk}`);
            setEditToken(tk);
            setForm(f => ({
              ...f,
              child_name: r2.data.child_name || '',
              parent_name: r2.data.parent_name || '',
              parent_phone: r2.data.parent_phone || '',
              can_attend: r2.data.can_attend || '',
              accompanying: r2.data.accompanying || [],
              accompanying_other: r2.data.accompanying_other || '',
              dietary: r2.data.dietary || '',
              dietary_other: r2.data.dietary_other || '',
              custom_responses: r2.data.custom_responses || {},
            }));
          } catch { /* invalid token — ignore */ }
        } else {
          // Auto-detect duplicate from localStorage
          try {
            const saved = localStorage.getItem(`rsvp_done_${slug}`);
            if (saved) {
              const obj = JSON.parse(saved);
              if (obj?.personal_token) setDuplicateInfo(obj);
            }
          } catch { /* noop */ }
          // Try to restore an auto-saved draft for this device
          if (!tk && draftTokenRef.current) {
            try {
              const r3 = await axios.get(`${API}/api/events/public/${slug}/rsvp/draft/${draftTokenRef.current}`);
              if (r3.data) {
                setForm(f => ({
                  ...f,
                  child_name: r3.data.child_name || '',
                  parent_name: r3.data.parent_name || '',
                  parent_phone: r3.data.parent_phone || '',
                  can_attend: r3.data.can_attend || '',
                  accompanying: r3.data.accompanying || [],
                  accompanying_other: r3.data.accompanying_other || '',
                  dietary: r3.data.dietary || '',
                  dietary_other: r3.data.dietary_other || '',
                  custom_responses: r3.data.custom_responses || {},
                }));
                if (r3.data.updated_at) setDraftSavedAt(r3.data.updated_at);
                draftStartedRef.current = true; // restored = treated as started
              }
            } catch { /* no draft yet — fine */ }
          }
        }
      } catch (e) {
        setError(e.response?.status === 404 ? 'This event was not found or is no longer active.' : 'Something went wrong. Please try again.');
      }
      setLoading(false);
    };
    fetchEvent();
  }, [slug]);

  // Subtle parallax for floating emojis (disabled on touch / reduced-motion)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if ('ontouchstart' in window) return;
    const handler = (e) => {
      const cx = window.innerWidth / 2;
      const cy = window.innerHeight / 2;
      setParallax({ x: (e.clientX - cx) / cx, y: (e.clientY - cy) / cy });
    };
    window.addEventListener('mousemove', handler);
    return () => window.removeEventListener('mousemove', handler);
  }, []);

  // Debounced draft auto-save (≈1.8s after the user stops typing).
  // Skipped in edit-mode (we're updating a real RSVP) and when no field touched.
  useEffect(() => {
    if (editToken) return; // not auto-saving when editing a real submitted RSVP
    if (submitted) return;
    if (!event) return;
    if (!draftTokenRef.current) return;
    // Did the user fill at least one meaningful field?
    const hasContent = (form.child_name || form.parent_phone || form.parent_name ||
      form.can_attend || (form.accompanying || []).length > 0 || form.dietary ||
      Object.values(form.custom_responses || {}).some(v => Array.isArray(v) ? v.length > 0 : !!v));
    if (!hasContent) return;
    draftStartedRef.current = true;
    setDraftStatus('saving');
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(async () => {
      try {
        const r = await axios.put(`${API}/api/events/public/${slug}/rsvp/draft`, {
          draft_token: draftTokenRef.current,
          child_name: form.child_name,
          parent_name: form.parent_name,
          parent_phone: form.parent_phone,
          can_attend: form.can_attend,
          accompanying: form.accompanying,
          accompanying_other: form.accompanying_other,
          dietary: form.dietary,
          dietary_other: form.dietary_other,
          custom_responses: form.custom_responses,
        });
        setDraftStatus('saved');
        setDraftSavedAt(r.data?.saved_at || new Date().toISOString());
      } catch {
        setDraftStatus('error');
      }
    }, 1800);
    return () => { if (draftTimerRef.current) clearTimeout(draftTimerRef.current); };
  }, [form, slug, event, editToken, submitted]);

  const handleAttendPick = (opt) => {
    setForm(f => ({ ...f, can_attend: opt }));
    if (opt.toLowerCase().includes('yes')) {
      setConfettiKey(k => k + 1);
      // Cake burst only on birthday-themed events
      const t = (event?.title || '').toLowerCase();
      if (t.includes('birthday') || t.includes('cake')) {
        setCakeKey(k => k + 1);
      }
    }
  };

  const handleAccompToggle = (val) => {
    setForm(f => ({
      ...f,
      accompanying: f.accompanying.includes(val)
        ? f.accompanying.filter(a => a !== val)
        : [...f.accompanying, val]
    }));
  };

  const handleCustomChange = (fieldId, value) => {
    setForm(f => ({ ...f, custom_responses: { ...f.custom_responses, [fieldId]: value } }));
  };

  const handleCustomCheckbox = (fieldId, option) => {
    setForm(f => {
      const current = f.custom_responses[fieldId] || [];
      const updated = current.includes(option) ? current.filter(o => o !== option) : [...current, option];
      return { ...f, custom_responses: { ...f.custom_responses, [fieldId]: updated } };
    });
  };

  const handleSubmit = async (e, opts = {}) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!form.child_name || !form.can_attend) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (editToken) payload.edit_token = editToken;
      if (draftTokenRef.current) payload.draft_token = draftTokenRef.current;
      if (opts.allowDuplicatePhone) payload.allow_duplicate_phone = true;
      const res = await axios.post(`${API}/api/events/public/${slug}/rsvp`, payload);
      setThankYou(res.data.message);
      setRsvpResult({
        rsvp_id: res.data.rsvp_id,
        personal_token: res.data.personal_token,
        gallery_enabled: res.data.gallery_enabled,
        gallery_face_match_enabled: res.data.gallery_face_match_enabled,
        waitlisted: res.data.waitlisted || false,
        was_update: res.data.status === 'updated',
      });
      // Persist in browser to detect repeat visits
      try {
        localStorage.setItem(`rsvp_done_${slug}`, JSON.stringify({
          personal_token: res.data.personal_token,
          child_name: form.child_name,
          can_attend: form.can_attend,
          submitted_at: new Date().toISOString(),
        }));
        // Clear the draft token so a brand-new RSVP next time doesn't resume stale data
        localStorage.removeItem(`rsvp_draft_token_${slug}`);
      } catch { /* noop */ }
      setSubmitted(true);
    } catch (err) {
      // 409 duplicate-phone — offer edit
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && typeof detail === 'object' && detail.code === 'duplicate_phone') {
        setDuplicateInfo({
          personal_token: detail.personal_token,
          child_name: detail.child_name,
          message: detail.message,
          same_child: !!detail.same_child,
        });
      } else if (err.response?.status === 409 && typeof detail === 'object' && detail.code === 'event_full') {
        setError(detail.message || 'Event is full.');
      } else {
        setError((typeof detail === 'string' ? detail : detail?.message) || 'Failed to submit. Please try again.');
      }
    }
    setSubmitting(false);
  };

  const bif = event?.built_in_fields || {};
  const theme = event?.theme || null;
  const themeColor = theme?.primary || event?.theme_color || '#FF6B35';
  const themeAccent = theme?.accent || themeColor;
  const themeBg = theme?.background || '#0A0A0A';
  const themeFont = theme?.font_family || "'Inter', system-ui, sans-serif";
  const themeEmojis = theme?.emojis || [];
  const decorations = theme?.decorations || [];
  const isLightBg = typeof themeBg === 'string' && themeBg.includes('linear-gradient') && !/(#0[0-9a-fA-F]{1})|#1[0-2]/i.test(themeBg);
  const headingColor = isLightBg ? '#1F1147' : '#FFFFFF';
  const subTextColor = isLightBg ? 'rgba(31,17,71,0.65)' : '#A3A3A3';
  const cardBg = isLightBg ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.03)';
  const cardBorder = isLightBg ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.08)';
  const inputBg = isLightBg ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.03)';
  const inputBorder = isLightBg ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)';
  const inputText = isLightBg ? '#1F1147' : '#FFFFFF';

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: themeBg }}>
      <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: themeColor, borderTopColor: 'transparent' }} />
    </div>
  );

  if (error && !event) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: themeBg }}>
      <div className="text-center max-w-sm">
        <Warning size={48} className="text-red-400 mx-auto mb-4" />
        <p className="text-base" style={{ color: isLightBg ? '#1F1147' : '#D4D4D4' }}>{error}</p>
      </div>
    </div>
  );

  // RSVPs closed by deadline
  const rsvpClosed = (() => {
    if (!event?.rsvp_closes_at) return false;
    try { return new Date(event.rsvp_closes_at) < new Date(); } catch { return false; }
  })();
  if (rsvpClosed && !editToken && !submitted) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: themeBg, fontFamily: themeFont }}>
      <div className="text-center max-w-md" data-testid="rsvp-closed-screen">
        <div className="text-6xl mb-4">{event?.theme_emoji || '🎉'}</div>
        <h1 className="text-2xl sm:text-3xl font-bold mb-3" style={{ color: headingColor }}>{event?.title}</h1>
        <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full mb-4" style={{ background: `${themeColor}25`, color: themeColor }}>
          <Clock size={12} weight="bold" />
          <span className="text-xs font-bold uppercase">RSVPs are closed</span>
        </div>
        <p className="text-sm" style={{ color: subTextColor }}>
          Hosts have closed responses for this event. Reach out to them directly if you need to be added.
        </p>
      </div>
    </div>
  );

  // Already RSVP'd (duplicate detection)
  if (duplicateInfo && !submitted && !editToken && event?.allow_edit_after_submit !== false) {
    const isSibling = !duplicateInfo.same_child && form.child_name && form.child_name.trim().toLowerCase() !== (duplicateInfo.child_name || '').trim().toLowerCase();
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: themeBg, fontFamily: themeFont }}>
        <div className="text-center max-w-md p-6 rounded-3xl"
          style={{ background: isLightBg ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.04)', border: `1px solid ${themeColor}30`, backdropFilter: 'blur(12px)' }}
          data-testid="already-rsvpd-screen">
          <div className="text-5xl mb-3">{isSibling ? '👨‍👩‍👧‍👦' : '✓'}</div>
          <h1 className="text-xl sm:text-2xl font-bold mb-2" style={{ color: headingColor }}>
            {isSibling ? 'RSVPing for another child?' : "You've already RSVP'd!"}
          </h1>
          <p className="text-sm mb-5" style={{ color: subTextColor }}>
            {isSibling ? (
              <>
                We already have an RSVP for <span style={{ color: themeColor, fontWeight: 700 }}>{duplicateInfo.child_name}</span> from this number.
                <br />Is this a new RSVP for <span style={{ color: themeColor, fontWeight: 700 }}>{form.child_name}</span>?
              </>
            ) : (
              <>
                {duplicateInfo.child_name && <>Your RSVP for <span style={{ color: themeColor, fontWeight: 700 }}>{duplicateInfo.child_name}</span> is saved.</>}
                {' '}Want to update your response?
              </>
            )}
          </p>
          <div className="flex flex-col gap-2">
            {isSibling ? (
              <>
                <button
                  onClick={() => { setDuplicateInfo(null); handleSubmit(null, { allowDuplicatePhone: true }); }}
                  data-testid="rsvp-sibling-submit"
                  disabled={submitting}
                  className="px-5 py-3 rounded-xl text-white text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
                  style={{ background: themeColor, boxShadow: `0 8px 24px ${themeColor}40` }}>
                  {submitting ? 'Submitting…' : `Yes, RSVP for ${form.child_name}`}
                </button>
                <button onClick={() => { window.location.href = `?edit=${duplicateInfo.personal_token}`; }}
                  data-testid="edit-existing-rsvp"
                  className="px-5 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-100 opacity-70"
                  style={{ color: subTextColor, background: 'transparent', border: `1px solid ${inputBorder}` }}>
                  No, edit my RSVP for {duplicateInfo.child_name}
                </button>
              </>
            ) : (
              <>
                <button onClick={() => { window.location.href = `?edit=${duplicateInfo.personal_token}`; }}
                  data-testid="edit-existing-rsvp"
                  className="px-5 py-3 rounded-xl text-white text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98]"
                  style={{ background: themeColor, boxShadow: `0 8px 24px ${themeColor}40` }}>
                  Edit my response
                </button>
                <button
                  onClick={() => { setDuplicateInfo(null); handleSubmit(null, { allowDuplicatePhone: true }); }}
                  data-testid="rsvp-fresh"
                  disabled={submitting}
                  className="px-5 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-100 opacity-70 disabled:opacity-40"
                  style={{ color: subTextColor, background: 'transparent' }}>
                  {submitting ? 'Submitting…' : 'Submit a new RSVP anyway'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (submitted) return (
    <div className="min-h-screen flex items-center justify-center px-4 relative overflow-hidden" style={{ background: themeBg, fontFamily: themeFont }}>
      <ThemeDecorations decorations={decorations} emojis={themeEmojis} themeColor={themeColor} parallax={parallax} />
      <ThemeParticles event={event} themeColor={themeColor} />
      <ConfettiShower active={form.can_attend.toLowerCase().includes('yes')} themeColor={themeColor} emojis={themeEmojis} />
      <div className="text-center max-w-md animate-fade-up relative z-10" data-testid="rsvp-thank-you">
        <div className="mb-6">
          <div className="w-20 h-20 rounded-full mx-auto flex items-center justify-center mb-4 thank-you-pop" style={{ background: `${themeColor}20`, border: `2px solid ${themeColor}50` }}>
            {form.can_attend.toLowerCase() === 'yes' ? (
              <Confetti size={36} weight="fill" style={{ color: themeColor }} />
            ) : (
              <Heart size={36} weight="fill" className="text-red-400" />
            )}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ color: headingColor, fontFamily: themeFont }}>
            {rsvpResult?.was_update ? "Response updated!" : (form.can_attend.toLowerCase() === 'yes' ? 'See you there!' : "We'll miss you!")}
          </h1>
          <p className="text-sm sm:text-base leading-relaxed" style={{ color: subTextColor }}>{thankYou}</p>
          {rsvpResult?.waitlisted && (
            <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold"
              style={{ background: '#FCD34D20', color: '#FBBF24', border: '1px solid #FBBF2440' }}
              data-testid="waitlist-banner">
              ⏳ You're on the waitlist — we'll confirm soon
            </div>
          )}
        </div>

        {form.can_attend.toLowerCase().includes('yes') && (
          <AddToCalendarButton event={event} themeColor={themeColor} />
        )}

        {/* Photo Wall + My Highlights links */}
        {rsvpResult?.gallery_enabled && (
          <GalleryAndSelfieSection
            slug={slug}
            rsvpResult={rsvpResult}
            event={event}
            themeColor={themeColor}
            isLightBg={isLightBg}
            headingColor={headingColor}
            subTextColor={subTextColor}
            cardBg={cardBg}
            cardBorder={cardBorder}
          />
        )}

        {form.can_attend.toLowerCase() === 'yes' && event?.venue && (
          <div className="mt-6 p-4 rounded-2xl" style={{ background: cardBg, border: `1px solid ${cardBorder}`, backdropFilter: 'blur(12px)' }}>
            <div className="flex items-center gap-2 mb-2">
              <MapPin size={16} style={{ color: themeColor }} />
              <span className="text-xs uppercase tracking-wider font-semibold" style={{ color: subTextColor }}>Venue</span>
            </div>
            <p className="text-sm font-medium" style={{ color: headingColor }}>{event.venue}</p>
            {event.venue_map_url && (
              <a href={event.venue_map_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs mt-2 font-semibold" style={{ color: themeColor }}>
                Open in Maps <MapPin size={12} />
              </a>
            )}
          </div>
        )}
        {form.can_attend.toLowerCase() === 'yes' && event?.event_date && (
          <div className="mt-5 p-5 rounded-2xl" style={{ background: cardBg, border: `1px solid ${cardBorder}`, backdropFilter: 'blur(12px)' }}>
            <PartyCountdown
              title={event.title}
              date={event.event_date}
              emoji={event.theme_emoji || themeEmojis[0]}
              color={themeColor}
              venue={event.venue}
              time={event.event_time}
            />
          </div>
        )}
      </div>
    </div>
  );

  if (event?.status === 'closed') return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: themeBg }}>
      <div className="text-center max-w-sm">
        <X size={48} className="text-neutral-500 mx-auto mb-4" />
        <p className="text-base" style={{ color: isLightBg ? '#1F1147' : '#D4D4D4' }}>RSVPs are now closed for this event.</p>
      </div>
    </div>
  );

  return (
    <div ref={pageRef} className="min-h-screen px-4 py-8 sm:py-12 relative overflow-hidden" style={{ background: themeBg, fontFamily: themeFont, '--rsvp-label-color': isLightBg ? '#1F1147' : '#E5E5E5', '--rsvp-theme-color': themeColor, '--rsvp-helper-pill-bg': isLightBg ? 'rgba(255,255,255,0.6)' : `${themeColor}20`, '--rsvp-helper-pill-fg': isLightBg ? '#1F1147' : themeColor, '--rsvp-helper-pill-border': isLightBg ? 'rgba(31,17,71,0.15)' : `${themeColor}40` }} data-testid="party-rsvp-page" data-no-theme-shim>
      {event.background_image && (
        <div aria-hidden className="pointer-events-none fixed inset-0 z-0" data-testid="rsvp-bg-image"
          style={{
            backgroundImage: `url(${event.background_image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
          }}>
          <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${event.background_overlay_opacity ?? 0.55})` }} />
        </div>
      )}
      <ThemeDecorations decorations={decorations} emojis={themeEmojis} themeColor={themeColor} parallax={parallax} />
      <ThemeParticles event={event} themeColor={themeColor} />
      <ConfettiBurst trigger={confettiKey} themeColor={themeColor} emojis={themeEmojis} />
      <CakeBurst trigger={cakeKey} themeColor={themeColor} />
      <div className="max-w-lg mx-auto relative z-10">
        {/* Header Card */}
        <div className="text-center mb-8">
          {event.theme_emoji && <div className="text-5xl mb-4 stagger-item" style={{ animationDelay: '0ms' }}>{event.theme_emoji}</div>}
          <h1 className="text-2xl sm:text-4xl font-bold leading-tight tracking-tight stagger-item" style={{ color: headingColor, fontFamily: themeFont, animationDelay: '80ms' }} data-testid="event-title">
            {event.title}
          </h1>
          {event.description && <p className="text-sm sm:text-base mt-3 leading-relaxed stagger-item" style={{ color: subTextColor, animationDelay: '160ms' }}>{event.description}</p>}
          {event.cover_message && <p className="text-sm mt-3 italic stagger-item" style={{ color: isLightBg ? 'rgba(31,17,71,0.85)' : '#D4D4D4', animationDelay: '240ms' }}>"{event.cover_message}"</p>}

          {/* Event Details Strip */}
          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 mt-6 stagger-item" style={{ animationDelay: '320ms' }}>
            <div className="flex items-center gap-2">
              <CalendarBlank size={16} style={{ color: themeColor }} />
              <span className="text-sm" style={{ color: isLightBg ? '#1F1147' : '#D4D4D4' }}>{formatDate(event.event_date)}</span>
            </div>
            {event.event_time && (
              <div className="flex items-center gap-2">
                <Clock size={16} style={{ color: themeColor }} />
                <span className="text-sm" style={{ color: isLightBg ? '#1F1147' : '#D4D4D4' }}>
                  {event.event_time}{event.end_time ? ` - ${event.end_time}` : ''}
                </span>
              </div>
            )}
            {event.venue && (
              <div className="flex items-center gap-2">
                <MapPin size={16} style={{ color: themeColor }} />
                <span className="text-sm" style={{ color: isLightBg ? '#1F1147' : '#D4D4D4' }}>{event.venue}</span>
              </div>
            )}
          </div>

          {/* Live countdown + guest counter strip */}
          <div className="mt-5 stagger-item" style={{ animationDelay: '400ms' }}>
            {event.show_countdown !== false && (() => {
              const threshold = event.countdown_hide_within_hours ?? 24;
              if (threshold > 0 && event.event_date) {
                try {
                  const target = new Date(`${event.event_date}T${event.event_time || '00:00'}:00`).getTime();
                  if (target - Date.now() < threshold * 3600 * 1000) return null;
                } catch { /* fallthrough */ }
              }
              return <InlineCountdown date={event.event_date} time={event.event_time} themeColor={themeColor} isLightBg={isLightBg} />;
            })()}
            {event.show_guest_counter !== false && (event.attending_count || 0) >= (event.guest_counter_min_rsvps ?? 3) && (
              <GuestCounter attending={event.attending_count} themeColor={themeColor} isLightBg={isLightBg} />
            )}
          </div>
        </div>

        {/* Edit mode banner */}
        {editToken && (
          <div className="p-3 rounded-2xl mb-3 stagger-item flex items-center gap-2"
            style={{ background: `${themeColor}20`, border: `1px solid ${themeColor}40`, animationDelay: '420ms' }}
            data-testid="edit-mode-banner">
            <PencilSimple size={14} weight="fill" style={{ color: themeColor }} />
            <span className="text-xs font-bold" style={{ color: themeColor }}>Editing your existing RSVP</span>
            <span className="text-[10px] ml-auto" style={{ color: subTextColor }}>Changes save when you click Submit</span>
          </div>
        )}

        {/* Auto-save pill — shown once the guest starts filling the form */}
        {!editToken && draftStartedRef.current && (
          <DraftSavePill status={draftStatus} savedAt={draftSavedAt} themeColor={themeColor} subTextColor={subTextColor} isLightBg={isLightBg} />
        )}

        {/* Event Resources (size charts, dress code, menus, etc.) */}
        {(event.attachments || []).length > 0 && (
          <EventResources attachments={event.attachments} themeColor={themeColor} isLightBg={isLightBg} />
        )}

        {/* RSVP Form */}
        <form onSubmit={handleSubmit} className="space-y-5 stagger-item" style={{ animationDelay: '480ms' }}>
          {error && <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-sm text-red-400">{error}</div>}

          {/* Child Name */}
          <FormField label={bif.child_name?.label || "Child's Name"} required>
            <input type="text" value={form.child_name} onChange={e => setForm(f => ({ ...f, child_name: e.target.value }))}
              data-testid="rsvp-child-name" placeholder="Enter child's name"
              className="rsvp-input" required />
          </FormField>

          {/* Parent Name */}
          {bif.parent_name?.enabled !== false && (
            <FormField label={bif.parent_name?.label || "Parent's Name"} required={bif.parent_name?.required}>
              <input type="text" value={form.parent_name} onChange={e => setForm(f => ({ ...f, parent_name: e.target.value }))}
                data-testid="rsvp-parent-name" placeholder="Your name"
                className="rsvp-input" required={bif.parent_name?.required} />
            </FormField>
          )}

          {/* Parent Phone */}
          {bif.parent_phone?.enabled !== false && (
            <FormField label={bif.parent_phone?.label || "Phone Number"} required={bif.parent_phone?.required}>
              <input type="tel" value={form.parent_phone} onChange={e => setForm(f => ({ ...f, parent_phone: e.target.value }))}
                data-testid="rsvp-parent-phone" placeholder="+91"
                className="rsvp-input" required={bif.parent_phone?.required} />
            </FormField>
          )}

          {/* Can Attend */}
          <FormField label={bif.can_attend?.label || "Can your child attend?"} required>
            <div className="grid grid-cols-2 gap-3">
              {(bif.can_attend?.options || ["Yes", "Sorry, can't make it"]).map(opt => (
                <button key={opt} type="button" onClick={() => handleAttendPick(opt)}
                  data-testid={`rsvp-attend-${opt.toLowerCase().replace(/[^a-z]/g, '')}`}
                  className={`p-3 rounded-xl text-sm font-semibold transition-all ${form.can_attend === opt
                    ? 'ring-2 text-white' : 'text-neutral-400 hover:text-white hover:bg-white/5'}`}
                  style={{
                    background: form.can_attend === opt ? `${themeColor}15` : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${form.can_attend === opt ? themeColor + '40' : 'rgba(255,255,255,0.08)'}`,
                    ringColor: form.can_attend === opt ? themeColor : 'transparent',
                  }}>
                  {opt.toLowerCase().includes('yes') && <Check size={14} className="inline mr-1" />}
                  {opt.toLowerCase().includes('sorry') && <X size={14} className="inline mr-1" />}
                  {opt}
                </button>
              ))}
            </div>
          </FormField>

          {/* Accompanying (show only if attending) */}
          {form.can_attend.toLowerCase().includes('yes') && (
            <>
              {bif.accompanying?.enabled !== false && (
              <FormField label={bif.accompanying?.label || "Who will be attending with your child?"} required={bif.accompanying?.required !== false}>
                <div className="flex flex-wrap gap-2">
                  {(bif.accompanying?.options || ["Mom", "Dad", "Parent + Nanny", "Nanny"]).map(opt => (
                    <button key={opt} type="button" onClick={() => handleAccompToggle(opt)}
                      data-testid={`rsvp-accomp-${opt.toLowerCase().replace(/[^a-z]/g, '')}`}
                      className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all ${form.accompanying.includes(opt)
                        ? 'text-white' : 'text-neutral-400 hover:text-white'}`}
                      style={{
                        background: form.accompanying.includes(opt) ? `${themeColor}15` : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${form.accompanying.includes(opt) ? themeColor + '40' : 'rgba(255,255,255,0.08)'}`,
                      }}>
                      {form.accompanying.includes(opt) && <Check size={12} className="inline mr-1" />}
                      {opt}
                    </button>
                  ))}
                </div>
                {bif.accompanying?.allow_other !== false && (
                  <input type="text" value={form.accompanying_other} onChange={e => setForm(f => ({ ...f, accompanying_other: e.target.value }))}
                    placeholder="Other (specify)" className="rsvp-input mt-2 text-xs" />
                )}
              </FormField>
              )}

              {/* Dietary */}
              {bif.dietary?.enabled !== false && (
              <FormField label={bif.dietary?.label || "Any allergies or dietary restrictions?"} required={bif.dietary?.required !== false}>
                <div className="flex flex-wrap gap-2">
                  {(bif.dietary?.options || ["Jain", "Veg", "Non-Veg", "No Restrictions"]).map(opt => (
                    <button key={opt} type="button" onClick={() => setForm(f => ({ ...f, dietary: opt }))}
                      data-testid={`rsvp-dietary-${opt.toLowerCase().replace(/[^a-z]/g, '')}`}
                      className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all ${form.dietary === opt
                        ? 'text-white' : 'text-neutral-400 hover:text-white'}`}
                      style={{
                        background: form.dietary === opt ? `${themeColor}15` : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${form.dietary === opt ? themeColor + '40' : 'rgba(255,255,255,0.08)'}`,
                      }}>
                      {form.dietary === opt && <Check size={12} className="inline mr-1" />}
                      {opt}
                    </button>
                  ))}
                </div>
                {bif.dietary?.allow_other !== false && (
                  <input type="text" value={form.dietary_other} onChange={e => setForm(f => ({ ...f, dietary_other: e.target.value }))}
                    placeholder="Other allergies or restrictions" className="rsvp-input mt-2 text-xs" />
                )}
              </FormField>
              )}

              {/* Custom Fields with conditional visibility (rules engine) */}
              {(event.custom_fields || []).map(cf => {
                if (!shouldShowField(cf, form)) return null;
                // Resolve linked event attachment → use as helper image (image) or PDF link
                let resolvedField = cf;
                if (cf.linked_attachment_id) {
                  const linked = (event.attachments || []).find(a => a.att_id === cf.linked_attachment_id);
                  if (linked) {
                    const isImg = (linked.mime || '').startsWith('image/');
                    resolvedField = {
                      ...cf,
                      helper_image: isImg ? linked.data_url : cf.helper_image,
                      helper_label: linked.name || cf.helper_label || '',
                      helper_text: cf.helper_text || (isImg ? '' : `See "${linked.name}" in Resources above`),
                    };
                  }
                }
                return (
                  <CustomFieldRenderer key={cf.field_id} field={resolvedField}
                    value={form.custom_responses[cf.field_id] || (cf.field_type === 'checkbox' ? [] : (cf.field_type === 'siblings' ? { attending: '', list: [] } : ''))}
                    onChange={(val) => handleCustomChange(cf.field_id, val)}
                    onCheckboxToggle={(opt) => handleCustomCheckbox(cf.field_id, opt)}
                    themeColor={themeColor}
                    slug={slug} />
                );
              })}
            </>
          )}

          {/* Submit */}
          <button type="submit" disabled={submitting || !form.child_name || !form.can_attend}
            data-testid="rsvp-submit-btn"
            className={`relative w-full py-4 rounded-2xl text-white font-bold text-base transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:hover:scale-100 overflow-hidden ${form.child_name && form.can_attend && !submitting ? 'btn-ready-pulse' : ''}`}
            style={{ background: `linear-gradient(135deg, ${themeColor}, ${themeColor}DD)`, boxShadow: `0 8px 30px ${themeColor}30` }}>
            <span className="absolute inset-0 btn-shimmer pointer-events-none" aria-hidden />
            {submitting ? (
              <span className="flex items-center justify-center gap-2 relative">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Submitting...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2 relative">
                <PaperPlaneTilt size={18} weight="fill" /> Submit RSVP
              </span>
            )}
          </button>

          <p className="text-center text-[10px] text-neutral-600 mt-3">{event.branding_footer || 'Powered by HomeCommand'}</p>
        </form>

        <style>{`
          .rsvp-input {
            width: 100%;
            padding: 12px 16px;
            border-radius: 12px;
            background: ${inputBg};
            border: 1px solid ${inputBorder};
            color: ${inputText};
            font-size: 14px;
            outline: none;
            transition: border-color 0.2s;
          }
          .rsvp-input:focus {
            border-color: ${themeColor}80;
            box-shadow: 0 0 0 3px ${themeColor}20;
          }
          .rsvp-input::placeholder { color: ${isLightBg ? 'rgba(31,17,71,0.4)' : '#525252'}; }
          ${isLightBg ? `
            [data-testid="party-rsvp-page"] button.text-neutral-400 {
              color: rgba(31,17,71,0.7) !important;
              background: rgba(255,255,255,0.7) !important;
              border-color: rgba(31,17,71,0.12) !important;
            }
            [data-testid="party-rsvp-page"] button.text-neutral-400:hover {
              color: ${themeColor} !important;
              background: rgba(255,255,255,0.9) !important;
            }
          ` : ''}
          @keyframes fade-up {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .animate-fade-up { animation: fade-up 0.5s ease-out forwards; }
          .stagger-item { opacity: 0; animation: fade-up 0.6s cubic-bezier(0.22,1,0.36,1) forwards; }
          @keyframes float-emoji {
            0%, 100% { transform: translateY(0) rotate(-5deg); }
            50% { transform: translateY(-12px) rotate(5deg); }
          }
          .floating-emoji { animation: float-emoji 4s ease-in-out infinite; }

          /* Submit button shimmer + ready pulse */
          @keyframes shimmer-sweep {
            0% { transform: translateX(-120%); }
            100% { transform: translateX(220%); }
          }
          .btn-shimmer {
            background: linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.25) 50%, transparent 100%);
            transform: translateX(-120%);
            animation: shimmer-sweep 2.6s ease-in-out infinite;
            mix-blend-mode: overlay;
          }
          @keyframes ready-pulse {
            0%, 100% { box-shadow: 0 8px 30px ${themeColor}30, 0 0 0 0 ${themeColor}40; }
            50% { box-shadow: 0 8px 30px ${themeColor}50, 0 0 0 10px ${themeColor}00; }
          }
          .btn-ready-pulse { animation: ready-pulse 2s ease-in-out infinite; }

          /* Field focus enhancement */
          .rsvp-input:focus {
            transform: translateY(-1px);
            box-shadow: 0 0 0 3px ${themeColor}30, 0 6px 20px ${themeColor}25 !important;
            transition: all 0.2s ease;
          }

          /* Confetti burst (one-shot, radial blast) */
          @keyframes burst {
            0% {
              opacity: 1;
              transform: translate(-50%, -50%) rotate(var(--rotation)) translate(0, 0);
            }
            100% {
              opacity: 0;
              transform: translate(-50%, -50%) rotate(var(--rotation)) translate(calc(cos(var(--angle)) * var(--distance)), calc(sin(var(--angle)) * var(--distance)));
            }
          }
          .confetti-piece {
            position: absolute;
            top: 50%;
            left: 50%;
            width: 8px;
            height: 14px;
            border-radius: 2px;
            transform: translate(-50%, -50%);
            animation: burst 1.1s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
          }

          /* Confetti shower (sustained fall) */
          @keyframes confetti-fall {
            from { transform: translateY(-20vh) rotate(0deg); opacity: 1; }
            to { transform: translateY(120vh) rotate(720deg); opacity: 0; }
          }
          .confetti-fall {
            position: absolute;
            top: -10vh;
            border-radius: 2px;
            animation: confetti-fall linear infinite;
          }

          /* Cake burst */
          @keyframes cake-burst-anim {
            0% { transform: scale(0.2); opacity: 0; }
            30% { transform: scale(1.6); opacity: 1; }
            60% { transform: scale(1.3); opacity: 1; }
            100% { transform: scale(2.2); opacity: 0; }
          }
          .cake-burst-emoji {
            font-size: 100px;
            animation: cake-burst-anim 1.4s cubic-bezier(0.22,1,0.36,1) forwards;
          }

          /* Thank-you bounce */
          @keyframes thank-pop {
            0% { transform: scale(0.2) rotate(-180deg); opacity: 0; }
            60% { transform: scale(1.15) rotate(8deg); opacity: 1; }
            100% { transform: scale(1) rotate(0); opacity: 1; }
          }
          .thank-you-pop { animation: thank-pop 0.8s cubic-bezier(0.22,1,0.36,1) forwards; }

          /* Theme particle layer */
          .rsvp-particle {
            position: absolute;
            top: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 999px;
            opacity: 0;
            animation-iteration-count: infinite;
            animation-timing-function: ease-in;
          }
          @keyframes rsvp-bubble {
            0% { transform: translateY(0) translateX(0); opacity: 0; }
            10% { opacity: 0.55; }
            90% { opacity: 0.45; }
            100% { transform: translateY(-115vh) translateX(20px); opacity: 0; }
          }
          .rsvp-particle-bubbles {
            background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.55), rgba(255,255,255,0) 60%), currentColor;
            border-radius: 999px;
            animation-name: rsvp-bubble;
          }
          @keyframes rsvp-sparkle {
            0% { transform: translateY(0) rotate(0deg) scale(0.5); opacity: 0; }
            20% { opacity: 0.85; }
            80% { opacity: 0.6; }
            100% { transform: translateY(-110vh) rotate(540deg) scale(1.2); opacity: 0; }
          }
          .rsvp-particle-sparkles {
            background: transparent !important;
            animation-name: rsvp-sparkle;
            text-shadow: 0 0 8px currentColor;
          }
          @keyframes rsvp-paw {
            0%, 100% { transform: translateY(0) translateX(0) rotate(-12deg); opacity: 0; }
            5% { opacity: 0.4; }
            50% { transform: translateY(-50vh) translateX(50px) rotate(12deg); opacity: 0.5; }
            95% { opacity: 0.3; }
          }
          .rsvp-particle-paws {
            background: transparent !important;
            animation-name: rsvp-paw;
            text-shadow: 0 1px 2px rgba(0,0,0,0.2);
          }
          @keyframes rsvp-star {
            0% { transform: translateY(0) rotate(0deg); opacity: 0; }
            15% { opacity: 0.8; }
            100% { transform: translateY(-115vh) rotate(360deg); opacity: 0; }
          }
          .rsvp-particle-stars {
            background: transparent !important;
            color: #FFD93D;
            animation-name: rsvp-star;
            text-shadow: 0 0 10px #FFD93D;
          }
          @keyframes rsvp-confetti-drift {
            0% { transform: translateY(0) rotate(0); opacity: 0; }
            10% { opacity: 0.5; }
            100% { transform: translateY(-115vh) rotate(540deg); opacity: 0; }
          }
          .rsvp-particle-confetti { animation-name: rsvp-confetti-drift; }

          @media (prefers-reduced-motion: reduce) {
            .stagger-item, .animate-fade-up, .btn-ready-pulse, .btn-shimmer, .confetti-piece, .confetti-fall, .rsvp-particle, .floating-emoji, .cake-burst-emoji, .thank-you-pop {
              animation: none !important;
              opacity: 1 !important;
              transform: none !important;
            }
          }
        `}</style>
      </div>
    </div>
  );
}

// Animated decorative background with floating theme emojis + mouse parallax
function ThemeDecorations({ decorations, emojis, themeColor, parallax = { x: 0, y: 0 } }) {
  if (!emojis || emojis.length === 0) return null;
  // Fixed-position emoji constellation
  const positions = [
    { top: '8%', left: '6%', size: 28, delay: '0s', depth: 18 },
    { top: '14%', right: '8%', size: 36, delay: '0.5s', depth: 24 },
    { top: '32%', left: '4%', size: 24, delay: '1s', depth: 12 },
    { top: '40%', right: '5%', size: 30, delay: '1.5s', depth: 20 },
    { top: '58%', left: '7%', size: 26, delay: '2s', depth: 16 },
    { top: '68%', right: '6%', size: 32, delay: '0.8s', depth: 22 },
    { bottom: '12%', left: '8%', size: 28, delay: '1.2s', depth: 14 },
    { bottom: '20%', right: '10%', size: 34, delay: '0.3s', depth: 26 },
  ];
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden" data-testid="theme-decorations">
      {decorations.includes('waves') && (
        <svg viewBox="0 0 1440 200" preserveAspectRatio="none" className="absolute bottom-0 left-0 w-full" style={{ height: 160, opacity: 0.45 }}>
          <path d="M0,100 C240,180 480,40 720,100 C960,160 1200,60 1440,100 L1440,200 L0,200 Z" fill={themeColor} opacity="0.45" />
          <path d="M0,140 C240,80 480,180 720,140 C960,100 1200,180 1440,140 L1440,200 L0,200 Z" fill={themeColor} opacity="0.65" />
        </svg>
      )}
      {positions.map((p, i) => (
        <div key={i} className="absolute"
          style={{
            top: p.top, bottom: p.bottom, left: p.left, right: p.right,
            transform: `translate(${parallax.x * p.depth}px, ${parallax.y * p.depth}px)`,
            transition: 'transform 200ms cubic-bezier(0.22, 1, 0.36, 1)',
            willChange: 'transform',
          }}>
          <div className="floating-emoji select-none"
            style={{
              fontSize: p.size, animationDelay: p.delay, opacity: 0.55,
              filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))',
            }}>
            {emojis[i % emojis.length]}
          </div>
        </div>
      ))}
    </div>
  );
}

function FormField({ label, required, helperText = '', helperImage = '', helperLabel = '', children }) {
  const [zoomOpen, setZoomOpen] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-2">
        <label className="block text-sm font-semibold flex-1" style={{ color: 'var(--rsvp-label-color, #D4D4D4)' }}>
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        {helperImage && (
          <button type="button" onClick={() => setZoomOpen(true)}
            data-testid="helper-image-thumb"
            className="relative shrink-0 inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full text-[11px] font-bold transition-all hover:scale-105 active:scale-95"
            style={{
              background: 'var(--rsvp-helper-pill-bg, rgba(0,0,0,0.15))',
              color: 'var(--rsvp-helper-pill-fg, currentColor)',
              border: '1px solid var(--rsvp-helper-pill-border, rgba(0,0,0,0.12))',
            }}>
            <span className="w-7 h-7 rounded-full overflow-hidden bg-white/40 shrink-0 flex items-center justify-center">
              <img src={helperImage} alt="" className="w-full h-full object-cover" />
            </span>
            <span className="pr-1">{helperLabel || 'View chart'}</span>
          </button>
        )}
      </div>
      {children}
      {helperText && (
        <p className="text-[11px] opacity-70 leading-relaxed" style={{ color: 'var(--rsvp-label-color, #A3A3A3)' }} data-testid="helper-text">
          {helperText}
        </p>
      )}
      {zoomOpen && helperImage && (
        <div className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4" data-testid="helper-image-zoom" onClick={() => setZoomOpen(false)}>
          <button onClick={() => setZoomOpen(false)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20">
            <X size={18} weight="bold" />
          </button>
          {helperLabel && (
            <div className="absolute top-4 left-4 text-white text-sm font-bold max-w-[60vw] truncate">{helperLabel}</div>
          )}
          <img src={helperImage} alt="reference" className="max-h-[90vh] max-w-full object-contain rounded-xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}

function CustomFieldRenderer({ field, value, onChange, onCheckboxToggle, themeColor, slug }) {
  switch (field.field_type) {
    case 'text':
      return (
        <FormField label={field.label} required={field.required} helperText={field.helper_text || ''} helperImage={field.helper_image || ''} helperLabel={field.helper_label || ''}>
          <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={field.placeholder}
            className="rsvp-input" required={field.required} data-testid={`cf-${field.field_id}`} />
        </FormField>
      );
    case 'textarea':
      return (
        <FormField label={field.label} required={field.required} helperText={field.helper_text || ''} helperImage={field.helper_image || ''} helperLabel={field.helper_label || ''}>
          <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={field.placeholder}
            className="rsvp-input min-h-[80px] resize-none" required={field.required} data-testid={`cf-${field.field_id}`} />
        </FormField>
      );
    case 'number':
      return (
        <FormField label={field.label} required={field.required} helperText={field.helper_text || ''} helperImage={field.helper_image || ''} helperLabel={field.helper_label || ''}>
          <input type="number" value={value} onChange={e => onChange(e.target.value)} placeholder={field.placeholder}
            className="rsvp-input" required={field.required} data-testid={`cf-${field.field_id}`} />
        </FormField>
      );
    case 'dropdown':
      return (
        <FormField label={field.label} required={field.required} helperText={field.helper_text || ''} helperImage={field.helper_image || ''} helperLabel={field.helper_label || ''}>
          <select value={value} onChange={e => onChange(e.target.value)}
            className="rsvp-input" required={field.required} data-testid={`cf-${field.field_id}`}>
            <option value="">Select...</option>
            {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </FormField>
      );
    case 'radio':
      return (
        <FormField label={field.label} required={field.required} helperText={field.helper_text || ''} helperImage={field.helper_image || ''} helperLabel={field.helper_label || ''}>
          <div className="flex flex-wrap gap-2">
            {field.options.map(opt => (
              <button key={opt} type="button" onClick={() => onChange(opt)}
                data-testid={`cf-${field.field_id}-${opt.toLowerCase().replace(/[^a-z]/g, '')}`}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all ${value === opt ? 'text-white' : 'text-neutral-400'}`}
                style={{
                  background: value === opt ? `${themeColor}15` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${value === opt ? themeColor + '40' : 'rgba(255,255,255,0.08)'}`,
                }}>
                {value === opt && <Check size={12} className="inline mr-1" />} {opt}
              </button>
            ))}
          </div>
        </FormField>
      );
    case 'checkbox':
      return (
        <FormField label={field.label} required={field.required} helperText={field.helper_text || ''} helperImage={field.helper_image || ''} helperLabel={field.helper_label || ''}>
          <div className="flex flex-wrap gap-2">
            {field.options.map(opt => (
              <button key={opt} type="button" onClick={() => onCheckboxToggle(opt)}
                data-testid={`cf-${field.field_id}-${opt.toLowerCase().replace(/[^a-z]/g, '')}`}
                className={`px-4 py-2.5 rounded-xl text-xs font-semibold transition-all ${(value || []).includes(opt) ? 'text-white' : 'text-neutral-400'}`}
                style={{
                  background: (value || []).includes(opt) ? `${themeColor}15` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${(value || []).includes(opt) ? themeColor + '40' : 'rgba(255,255,255,0.08)'}`,
                }}>
                {(value || []).includes(opt) && <Check size={12} className="inline mr-1" />} {opt}
              </button>
            ))}
          </div>
        </FormField>
      );
    case 'yesno':
      return (
        <FormField label={field.label} required={field.required} helperText={field.helper_text || ''} helperImage={field.helper_image || ''} helperLabel={field.helper_label || ''}>
          <div className="flex gap-3">
            {['Yes', 'No'].map(opt => (
              <button key={opt} type="button" onClick={() => onChange(opt)}
                data-testid={`cf-${field.field_id}-${opt.toLowerCase()}`}
                className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${value === opt ? 'text-white' : 'text-neutral-400'}`}
                style={{
                  background: value === opt ? `${themeColor}15` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${value === opt ? themeColor + '40' : 'rgba(255,255,255,0.08)'}`,
                }}>
                {opt}
              </button>
            ))}
          </div>
        </FormField>
      );
    case 'image':
      return <ImageFieldUploader field={field} value={value} onChange={onChange} themeColor={themeColor} slug={slug} />;
    case 'siblings':
      return <SiblingsField field={field} value={value} onChange={onChange} themeColor={themeColor} />;
    default:
      return null;
  }
}

function SiblingsField({ field, value, onChange, themeColor }) {
  // value shape: { attending: 'Yes'|'No'|'', list: [{name, age}] }
  const v = (value && typeof value === 'object' && !Array.isArray(value)) ? value : { attending: '', list: [] };
  const attending = v.attending || '';
  const list = Array.isArray(v.list) ? v.list : [];

  const setAttending = (opt) => {
    if (opt === 'Yes') {
      const nextList = list.length > 0 ? list : [{ name: '', age: '' }];
      onChange({ attending: 'Yes', list: nextList });
    } else {
      onChange({ attending: 'No', list: [] });
    }
  };

  const updateSibling = (idx, patch) => {
    const next = list.map((s, i) => i === idx ? { ...s, ...patch } : s);
    onChange({ attending: 'Yes', list: next });
  };

  const addSibling = () => onChange({ attending: 'Yes', list: [...list, { name: '', age: '' }] });
  const removeSibling = (idx) => {
    const next = list.filter((_, i) => i !== idx);
    onChange({ attending: next.length > 0 ? 'Yes' : '', list: next });
  };

  return (
    <FormField label={field.label} required={field.required} helperText={field.helper_text || ''} helperImage={field.helper_image || ''} helperLabel={field.helper_label || ''}>
      <div className="flex gap-3 mb-2">
        {['Yes', 'No'].map(opt => (
          <button key={opt} type="button" onClick={() => setAttending(opt)}
            data-testid={`cf-${field.field_id}-${opt.toLowerCase()}`}
            className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${attending === opt ? 'text-white' : 'text-neutral-400'}`}
            style={{
              background: attending === opt ? `${themeColor}15` : 'rgba(255,255,255,0.03)',
              border: `1px solid ${attending === opt ? themeColor + '40' : 'rgba(255,255,255,0.08)'}`,
            }}>
            {opt}
          </button>
        ))}
      </div>
      {attending === 'Yes' && (
        <div className="space-y-2 mt-2">
          {list.map((sib, idx) => (
            <div key={idx} className="grid grid-cols-[1fr_90px_36px] gap-2 items-center" data-testid={`cf-${field.field_id}-row-${idx}`}>
              <input type="text" value={sib.name || ''} onChange={e => updateSibling(idx, { name: e.target.value })}
                placeholder={`Sibling ${idx + 1} name`}
                data-testid={`cf-${field.field_id}-name-${idx}`}
                className="rsvp-input" />
              <input type="number" min="0" max="25" step="0.5" value={sib.age ?? ''}
                onChange={e => updateSibling(idx, { age: e.target.value })}
                placeholder="Age"
                data-testid={`cf-${field.field_id}-age-${idx}`}
                className="rsvp-input" />
              {list.length > 1 ? (
                <button type="button" onClick={() => removeSibling(idx)}
                  data-testid={`cf-${field.field_id}-remove-${idx}`}
                  className="p-2 rounded-lg text-neutral-500 hover:text-red-400 hover:bg-red-500/10 flex items-center justify-center">
                  <X size={14} weight="bold" />
                </button>
              ) : (<span />)}
            </div>
          ))}
          <button type="button" onClick={addSibling}
            data-testid={`cf-${field.field_id}-add`}
            className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-1.5 rounded-lg"
            style={{ background: `${themeColor}15`, color: themeColor, border: `1px solid ${themeColor}30` }}>
            + Add another sibling
          </button>
        </div>
      )}
    </FormField>
  );
}


function ImageFieldUploader({ field, value, onChange, themeColor, slug }) {
  const inputRef = React.useRef(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('Image too large (5MB max)'); return; }
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/events/public/${slug}/rsvp-image`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(res.data.data_url);
    } catch (e) {
      setError(e.response?.data?.detail || 'Upload failed');
    }
    setUploading(false);
  };

  return (
    <FormField label={field.label} required={field.required} helperText={field.helper_text || ''} helperImage={field.helper_image || ''} helperLabel={field.helper_label || ''}>
      {value ? (
        <div className="relative rounded-2xl overflow-hidden" data-testid={`cf-image-preview-${field.field_id}`}>
          <img src={value} alt="Uploaded" className="w-full max-h-64 object-cover" />
          <div className="absolute top-2 right-2 flex gap-2">
            <button type="button" onClick={() => inputRef.current?.click()}
              className="px-3 py-1.5 rounded-lg bg-black/60 text-white text-[10px] font-bold">Replace</button>
            <button type="button" onClick={() => onChange('')}
              className="px-3 py-1.5 rounded-lg bg-black/60 text-red-300 text-[10px] font-bold">Remove</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
          data-testid={`cf-image-upload-${field.field_id}`}
          className="w-full py-6 rounded-2xl border-2 border-dashed transition-colors hover:scale-[1.01]"
          style={{
            background: 'rgba(255,255,255,0.03)',
            borderColor: uploading ? themeColor : 'rgba(255,255,255,0.15)',
            color: uploading ? themeColor : 'rgba(255,255,255,0.7)',
          }}>
          {uploading ? (
            <span className="flex items-center justify-center gap-2 text-xs font-bold">
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              Uploading...
            </span>
          ) : (
            <span className="flex flex-col items-center gap-1 text-xs font-semibold">
              <span className="text-2xl">📷</span>
              Tap to upload a photo (max 5MB)
            </span>
          )}
        </button>
      )}
      <input ref={inputRef} type="file" accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif"
        onChange={e => handleFile(e.target.files?.[0])}
        className="hidden" />
      {error && <p className="text-[11px] text-red-300 mt-1.5">{error}</p>}
    </FormField>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return dateStr; }
}


// ---------- VISUAL ENHANCEMENT COMPONENTS ----------

// Tier 2: Theme-aware ambient particle layer
function ThemeParticles({ event, themeColor }) {
  const decorations = event?.theme?.decorations || [];
  const title = (event?.title || '').toLowerCase();
  const themeId = (event?.theme_id || '').toLowerCase();

  let kind = 'confetti'; // default soft drift
  if (decorations.includes('waves') || /pool|splash|mermaid|water|swim/.test(title + themeId)) kind = 'bubbles';
  else if (decorations.includes('sparkles') || /princess|fairy|magic|unicorn|girly/.test(title + themeId)) kind = 'sparkles';
  else if (decorations.includes('paw-prints') || /paw|patrol|puppy|dog|cat|pet/.test(title + themeId)) kind = 'paws';
  else if (decorations.includes('stars') || /space|galaxy|star/.test(title + themeId)) kind = 'stars';

  const items = Array.from({ length: 18 });
  const cls = `rsvp-particle rsvp-particle-${kind}`;

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden z-0" data-testid={`theme-particles-${kind}`}>
      {items.map((_, i) => {
        const left = (i * 37 + 13) % 100;
        const delay = (i % 9) * 0.6;
        const dur = 8 + (i % 7);
        const size = 8 + (i % 5) * 3;
        return (
          <span key={i} className={cls}
            style={{
              left: `${left}%`,
              width: size, height: size,
              animationDelay: `${delay}s`,
              animationDuration: `${dur}s`,
              backgroundColor: themeColor,
              fontSize: size * 1.6,
            }}>
            {kind === 'sparkles' && '✨'}
            {kind === 'paws' && '🐾'}
            {kind === 'stars' && '★'}
          </span>
        );
      })}
    </div>
  );
}

// Tier 1: Confetti burst on "Yes" pick (one-shot, mounted via key prop change)
function ConfettiBurst({ trigger, themeColor, emojis = [] }) {
  if (!trigger) return null;
  const pieces = Array.from({ length: 36 });
  const palette = [themeColor, '#FFD93D', '#FF6B6B', '#4ECDC4', '#A78BFA', '#FFFFFF'];
  return (
    <div key={trigger} aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden z-30" data-testid="confetti-burst">
      {pieces.map((_, i) => {
        const angle = (i * 10) % 360;
        const distance = 120 + (i % 4) * 50;
        const useEmoji = i % 5 === 0 && emojis.length > 0;
        return (
          <span key={i} className="confetti-piece"
            style={{
              '--angle': `${angle}deg`,
              '--distance': `${distance}px`,
              '--rotation': `${(i * 47) % 720}deg`,
              animationDelay: `${(i % 6) * 0.04}s`,
              background: useEmoji ? 'transparent' : palette[i % palette.length],
              fontSize: useEmoji ? 18 : 0,
            }}>
            {useEmoji ? emojis[i % emojis.length] : ''}
          </span>
        );
      })}
    </div>
  );
}

// Tier 1: Sustained confetti shower on Thank-You screen
function ConfettiShower({ active, themeColor, emojis = [] }) {
  if (!active) return null;
  const pieces = Array.from({ length: 50 });
  const palette = [themeColor, '#FFD93D', '#FF6B6B', '#4ECDC4', '#A78BFA', '#FFFFFF'];
  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden z-30" data-testid="confetti-shower">
      {pieces.map((_, i) => {
        const useEmoji = i % 7 === 0 && emojis.length > 0;
        return (
          <span key={i} className="confetti-fall"
            style={{
              left: `${(i * 71 + 7) % 100}%`,
              background: useEmoji ? 'transparent' : palette[i % palette.length],
              width: useEmoji ? 'auto' : 8,
              height: useEmoji ? 'auto' : 14,
              fontSize: useEmoji ? 18 : 0,
              animationDelay: `${(i % 10) * 0.3}s`,
              animationDuration: `${4 + (i % 5)}s`,
            }}>
            {useEmoji ? emojis[i % emojis.length] : ''}
          </span>
        );
      })}
    </div>
  );
}

// Tier 2: Cake-cut moment for birthday events
function CakeBurst({ trigger, themeColor }) {
  if (!trigger) return null;
  return (
    <div key={trigger} aria-hidden className="pointer-events-none fixed inset-0 flex items-center justify-center z-40" data-testid="cake-burst">
      <div className="cake-burst-emoji" style={{ filter: `drop-shadow(0 8px 30px ${themeColor}90)` }}>🎂</div>
    </div>
  );
}

// Tier 1: Live countdown chip above the form
function InlineCountdown({ date, time, themeColor, isLightBg }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);
  if (!date) return null;
  let target;
  try {
    const t = (time || '00:00').match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
    let h = t ? parseInt(t[1], 10) : 0;
    const m = t && t[2] ? parseInt(t[2], 10) : 0;
    if (t && /pm/i.test(t[3] || '') && h < 12) h += 12;
    if (t && /am/i.test(t[3] || '') && h === 12) h = 0;
    target = new Date(`${date}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`).getTime();
  } catch { return null; }
  const diff = target - now;
  if (diff <= 0) return (
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold animate-pulse" style={{ background: `${themeColor}25`, color: themeColor }} data-testid="rsvp-countdown-live">
      <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 999, background: themeColor }} />
      Happening now!
    </div>
  );
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const label = days > 1 ? `${days} days to go` : days === 1 ? `Tomorrow! ${hours}h ${mins}m` : hours > 0 ? `${hours}h ${mins}m left` : `${mins} min left`;
  return (
    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold" style={{ background: isLightBg ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.06)', color: themeColor, border: `1px solid ${themeColor}30` }} data-testid="rsvp-countdown">
      <Clock size={12} weight="bold" />
      {label}
    </div>
  );
}

// Tier 3: Social-proof guest counter
function GuestCounter({ attending, themeColor, isLightBg }) {
  if (!attending || attending <= 0) return null;
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold ml-2" style={{ background: isLightBg ? 'rgba(255,255,255,0.7)' : 'rgba(255,255,255,0.06)', color: themeColor, border: `1px solid ${themeColor}30` }} data-testid="rsvp-guest-counter">
      <Sparkle size={11} weight="fill" />
      {attending} {attending === 1 ? 'family' : 'families'} already coming ✓
    </div>
  );
}

// Tier 3: Add-to-Calendar button (generates .ics file client-side, no library)
function AddToCalendarButton({ event, themeColor }) {
  const [added, setAdded] = useState(false);
  const handleClick = () => {
    if (!event?.event_date) return;
    const tParse = (s) => {
      if (!s) return [0, 0];
      const m = s.match(/(\d{1,2}):?(\d{2})?\s*(AM|PM)?/i);
      if (!m) return [0, 0];
      let h = parseInt(m[1], 10); const min = m[2] ? parseInt(m[2], 10) : 0;
      if (/pm/i.test(m[3] || '') && h < 12) h += 12;
      if (/am/i.test(m[3] || '') && h === 12) h = 0;
      return [h, min];
    };
    const [sh, sm] = tParse(event.event_time);
    const [eh, em] = event.end_time ? tParse(event.end_time) : [sh + 2, sm];
    const fmt = (h, m) => {
      const d = new Date(`${event.event_date}T${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:00`);
      return d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };
    const dt = `DTSTART:${fmt(sh, sm)}\nDTEND:${fmt(eh, em)}`;
    const ics = [
      'BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//HomeCommand//RSVP//EN',
      'BEGIN:VEVENT',
      `UID:${event.event_id || 'rsvp'}@homecontrol.in`,
      `SUMMARY:${(event.title || 'Event').replace(/[\n\r,;]/g, ' ')}`,
      dt,
      event.venue ? `LOCATION:${event.venue.replace(/[\n\r,;]/g, ' ')}` : '',
      event.description ? `DESCRIPTION:${event.description.replace(/\n/g, '\\n').replace(/[,;]/g, ' ')}` : '',
      'END:VEVENT', 'END:VCALENDAR',
    ].filter(Boolean).join('\r\n');
    const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${(event.title || 'event').replace(/[^a-z0-9]/gi, '_')}.ics`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    setAdded(true);
    setTimeout(() => setAdded(false), 2500);
  };
  return (
    <button type="button" onClick={handleClick} data-testid="add-to-calendar-btn"
      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-xs font-bold transition-all hover:scale-[1.03] active:scale-[0.97] mt-3"
      style={{ background: themeColor, boxShadow: `0 8px 24px ${themeColor}40` }}>
      {added ? (
        <>
          <Check size={14} weight="bold" /> Added!
        </>
      ) : (
        <>
          <DownloadSimple size={14} weight="bold" /> Add to Calendar
        </>
      )}
    </button>
  );
}


// ---- Gallery + Selfie / Highlight reel section on Thank-You screen ----
function GalleryAndSelfieSection({ slug, rsvpResult, event, themeColor, isLightBg, headingColor, subTextColor, cardBg, cardBorder }) {
  const personalUrl = `${window.location.origin}/party/${slug}/my-photos/${rsvpResult.personal_token}`;
  const galleryUrl = `${window.location.origin}/party/${slug}/gallery`;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [refStatus, setRefStatus] = useState('idle'); // idle | done | error
  const [refError, setRefError] = useState('');
  const [copied, setCopied] = useState(false);

  const handleSelfie = async (file) => {
    if (!file) return;
    setUploading(true);
    setRefStatus('idle');
    setRefError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(
        `${API}/api/events/public/${slug}/rsvp/${rsvpResult.rsvp_id}/face-ref`,
        fd, { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setRefStatus('done');
    } catch (e) {
      setRefStatus('error');
      setRefError(e.response?.data?.detail || 'Could not detect a face. Try again with better light.');
    }
    setUploading(false);
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(personalUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* noop */ }
  };

  return (
    <div className="mt-6 p-5 rounded-2xl text-left" data-testid="gallery-selfie-section"
      style={{ background: cardBg, border: `1px solid ${cardBorder}`, backdropFilter: 'blur(12px)' }}>
      <div className="flex items-center gap-2 mb-2">
        <Sparkle size={16} weight="fill" style={{ color: themeColor }} />
        <span className="text-xs uppercase tracking-wider font-bold" style={{ color: themeColor }}>
          {rsvpResult.gallery_face_match_enabled ? 'Your private highlight reel' : 'Party photo wall'}
        </span>
      </div>

      {rsvpResult.gallery_face_match_enabled ? (
        <>
          <p className="text-sm leading-relaxed mb-3" style={{ color: subTextColor }}>
            After the party, see <span style={{ color: headingColor, fontWeight: 600 }}>only the photos you&apos;re in</span>.
            Upload a clear selfie now so we can pick you out automatically.
          </p>

          {refStatus === 'done' ? (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold"
              style={{ background: `${themeColor}20`, color: themeColor }} data-testid="selfie-success">
              <Check size={14} weight="bold" /> Selfie saved. We&apos;ll find you in the photos!
            </div>
          ) : (
            <button type="button" disabled={uploading} onClick={() => fileRef.current?.click()}
              data-testid="upload-selfie-btn"
              className="w-full px-4 py-3 rounded-xl text-white text-sm font-bold transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-60"
              style={{ background: themeColor, boxShadow: `0 8px 24px ${themeColor}40` }}>
              {uploading ? (
                <span className="inline-flex items-center gap-2">
                  <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Looking for your face...
                </span>
              ) : (
                <span className="inline-flex items-center gap-2"><Star size={14} weight="fill" /> Take a selfie</span>
              )}
            </button>
          )}
          {refStatus === 'error' && (
            <p className="text-xs mt-2 text-red-300" data-testid="selfie-error">{refError}</p>
          )}
          <input ref={fileRef} type="file" accept="image/*" capture="user" className="hidden"
            onChange={(e) => handleSelfie(e.target.files?.[0])} />

          <div className="mt-3 text-[10px] leading-relaxed" style={{ color: subTextColor }}>
            We store only a math signature of your face (no photo). You can revoke this any time from your personal link.
          </div>
        </>
      ) : (
        <p className="text-sm leading-relaxed mb-3" style={{ color: subTextColor }}>
          A public gallery will be live after the event.
        </p>
      )}

      {/* Personal link */}
      <div className="mt-4 pt-4 border-t" style={{ borderColor: cardBorder }}>
        <div className="text-[10px] uppercase tracking-wider mb-1.5 font-bold" style={{ color: subTextColor }}>
          Bookmark your link
        </div>
        <div className="flex items-center gap-2">
          <input readOnly value={personalUrl} data-testid="personal-link-input"
            className="flex-1 px-3 py-2 rounded-lg text-[11px] truncate"
            style={{ background: isLightBg ? 'rgba(0,0,0,0.04)' : 'rgba(0,0,0,0.3)', color: headingColor, border: `1px solid ${cardBorder}` }}
            onFocus={(e) => e.target.select()} />
          <button type="button" onClick={handleCopy} data-testid="copy-personal-link"
            className="px-3 py-2 rounded-lg text-[11px] font-bold whitespace-nowrap transition-all hover:scale-105"
            style={{ background: themeColor, color: '#fff' }}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
        <div className="mt-2 flex gap-2">
          <a href={personalUrl} target="_blank" rel="noopener noreferrer" data-testid="open-my-photos"
            className="text-[11px] font-bold inline-flex items-center gap-1" style={{ color: themeColor }}>
            Open my photos →
          </a>
          <span style={{ color: subTextColor }}>·</span>
          <a href={galleryUrl} target="_blank" rel="noopener noreferrer" data-testid="open-gallery"
            className="text-[11px] font-bold inline-flex items-center gap-1" style={{ color: themeColor }}>
            View full gallery →
          </a>
        </div>
      </div>
    </div>
  );
}


// ---- Event Resources panel (size charts, dress code, menu, etc.) ----
function EventResources({ attachments, themeColor, isLightBg }) {
  const [zoom, setZoom] = useState(null);
  const cardBg = isLightBg ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.03)';
  const cardBorder = isLightBg ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.08)';
  const textColor = isLightBg ? '#1F1147' : '#FFFFFF';
  const subText = isLightBg ? 'rgba(31,17,71,0.65)' : '#A3A3A3';
  return (
    <div className="p-4 rounded-2xl stagger-item" data-testid="event-resources"
      style={{ background: cardBg, border: `1px solid ${cardBorder}`, backdropFilter: 'blur(12px)', animationDelay: '440ms' }}>
      <div className="flex items-center gap-2 mb-3">
        <Star size={14} weight="fill" style={{ color: themeColor }} />
        <span className="text-xs uppercase tracking-wider font-bold" style={{ color: themeColor }}>Resources</span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {attachments.map((a) => {
          const isImg = (a.mime || '').startsWith('image/');
          const isPdf = (a.mime || '') === 'application/pdf';
          const open = () => {
            if (isImg) setZoom(a);
            else if (isPdf) window.open(a.data_url, '_blank', 'noopener,noreferrer');
          };
          return (
            <button key={a.att_id} type="button" onClick={open}
              data-testid={`resource-${a.att_id}`}
              className="text-left rounded-xl p-2 transition-all hover:scale-[1.03] active:scale-[0.97]"
              style={{ background: 'rgba(0,0,0,0.2)', border: `1px solid ${cardBorder}` }}>
              {isImg ? (
                <div className="w-full aspect-[4/3] rounded-lg overflow-hidden bg-neutral-900 mb-1.5">
                  <img src={a.data_url} alt={a.name} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-full aspect-[4/3] rounded-lg flex items-center justify-center text-[10px] font-bold mb-1.5"
                  style={{ background: `${themeColor}15`, color: themeColor }}>
                  {isPdf ? 'PDF' : 'FILE'}
                </div>
              )}
              <p className="text-xs font-bold truncate" style={{ color: textColor }}>{a.name}</p>
              {a.description && <p className="text-[10px] opacity-70 truncate" style={{ color: subText }}>{a.description}</p>}
            </button>
          );
        })}
      </div>
      {zoom && (
        <div className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4"
          data-testid="resource-zoom" onClick={() => setZoom(null)}>
          <button onClick={() => setZoom(null)} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20">
            <X size={18} weight="bold" />
          </button>
          <div className="absolute top-4 left-4 text-white text-sm font-bold max-w-[60vw] truncate">{zoom.name}</div>
          <img src={zoom.data_url} alt={zoom.name} className="max-h-[90vh] max-w-full object-contain rounded-xl" onClick={e => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}


// Tiny "auto-saving / saved a moment ago" pill above the form.
function DraftSavePill({ status, savedAt, themeColor, subTextColor, isLightBg }) {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force(n => n + 1), 30000);
    return () => clearInterval(t);
  }, []);
  const fmtRel = (iso) => {
    if (!iso) return '';
    try {
      const diffSec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
      if (diffSec < 5) return 'just now';
      if (diffSec < 60) return `${diffSec}s ago`;
      const min = Math.floor(diffSec / 60);
      if (min < 60) return `${min} min ago`;
      const hr = Math.floor(min / 60);
      if (hr < 24) return `${hr}h ago`;
      return new Date(iso).toLocaleDateString();
    } catch { return ''; }
  };
  const label =
    status === 'saving' ? 'Saving your progress…' :
    status === 'error'  ? 'Could not save — check your connection' :
    savedAt             ? `Saved ${fmtRel(savedAt)}` :
                          'Auto-saving your progress';
  const color =
    status === 'error' ? '#EF4444' :
    status === 'saving' ? themeColor :
    '#22C55E';
  return (
    <div className="px-3 py-2 rounded-2xl mb-3 stagger-item flex items-center gap-2"
      style={{
        background: isLightBg ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.04)',
        border: `1px solid ${color}30`,
        animationDelay: '440ms',
      }}
      data-testid="draft-autosave-pill">
      <span className="relative inline-flex">
        <span className={`w-1.5 h-1.5 rounded-full ${status === 'saving' ? 'animate-ping absolute' : ''}`} style={{ background: color }} />
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      </span>
      <span className="text-[11px] font-semibold" style={{ color }}>{label}</span>
      <span className="text-[10px] ml-auto" style={{ color: subTextColor }}>So you don't lose what you've typed</span>
    </div>
  );
}
